/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect } from 'react';

import { css, jsx } from '@compiled/react';
import { bind } from 'bind-event-listener';

import { getDocument } from '@atlaskit/browser-apis';
import { Anchor } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const styles = css({
	display: 'flex',
	position: 'fixed',
	zIndex: -1,
	gap: token('space.050'),
	flexDirection: 'column',
	backgroundColor: token('elevation.surface.overlay'),
	borderRadius: token('radius.small'),
	boxShadow: token('elevation.shadow.overlay'),
	insetBlockStart: token('space.250'),
	insetInlineStart: token('space.250'),
	opacity: 0,
	paddingBlock: token('space.150'),
	paddingInline: token('space.150'),
	pointerEvents: 'none',
	'&:focus-within': {
		zIndex: 'calc(infinity)',
		opacity: 1,
		pointerEvents: 'auto',
	},
});

const skipLinkListStyles = css({
	display: 'flex',
	gap: token('space.050'),
	flexDirection: 'column',
	listStylePosition: 'outside',
	listStyleType: 'none',
	marginBlockStart: token('space.0'),
	paddingInlineStart: token('space.0'),
});

const skipLinkListItemStyles = css({
	marginBlockStart: token('space.0'),
});

/**
 * Used for moving focus to the corresponding target after clicking on a skip link.
 */
function focusElement(element: HTMLElement) {
	/**
	 * Elements without an explicit `tabindex` attribute are not guaranteed to be focusable:
	 * https://html.spec.whatwg.org/multipage/interaction.html#attr-tabindex
	 *
	 * Our targets are not interactive, so this is required.
	 */
	element.setAttribute('tabindex', '-1');

	/**
	 * Cleanup the `tabindex` attribute we set when the target loses focus.
	 */
	bind(element, {
		type: 'blur',
		listener() {
			element.removeAttribute('tabindex');
		},
		options: {
			// Using a one-time listener so it cleans itself up
			once: true,
		},
	});

	/**
	 * Move focus to the target.
	 *
	 * Calling `.focus()` will also scroll the element into view:
	 * https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/focus
	 */
	element.focus({
		// Forces the focus ring to appear after moving focus to the target
		// https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/focus#focusvisible
		// @ts-ignore - new and not in types yet. Can be removed once we adopt ts7.
		focusVisible: true,
	});

	/**
	 * Explicitly scroll the element into view to ensure it's visible.
	 * This is especially important for elements like the footer at the bottom of the page.
	 */
	element.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/**
 * Keydown handler to hide the skip links when escape is pressed.
 */
const closeOnEscape = (event: KeyboardEvent) => {
	if (event.key !== 'Escape') {
		return;
	}

	const doc = getDocument();
	const activeElement = doc?.activeElement;
	if (activeElement instanceof HTMLElement) {
		activeElement.blur();
	}
};

type SkipLinkItem = {
	id: string;
	label: string;
};

/**
 * A simplified skip links component for the home page.
 * Appears on focus and provides links to main content and footer.
 */
export const HomeSkipLinks = ({ links }: { links: SkipLinkItem[] }): JSX.Element | null => {
	const handleClick = useCallback((event: React.MouseEvent<HTMLAnchorElement>, id: string) => {
		event.preventDefault();

		// Intentionally not using `document.querySelector` because many valid IDs are not valid selectors.
		const doc = getDocument();
		const target = doc?.getElementById(id);
		if (!target) {
			return;
		}

		focusElement(target);
	}, []);

	// Handle Escape key at document level to avoid making the container div interactive
	useEffect(() => {
		const doc = getDocument();
		if (!doc) {
			return;
		}

		return bind(doc, {
			type: 'keydown',
			listener: closeOnEscape,
		});
	}, []);

	if (links.length === 0) {
		return null;
	}

	return (
		<div css={styles}>
			<ol css={skipLinkListStyles}>
				{links.map(({ id, label }) => (
					<li key={id} css={skipLinkListItemStyles}>
						<Anchor
							/**
							 * It looks like Safari handles link clicks during `pointerdown` unless it has an explicit `tabIndex={0}` :/
							 *
							 * Adding this explicitly makes the behavior consistent between browsers and lets us `event.preventDefault()`
							 * in the `onClick` handler.
							 */
							tabIndex={0}
							href={`#${id}`}
							onClick={(event) => handleClick(event, id)}
						>
							{label}
						</Anchor>
					</li>
				))}
			</ol>
		</div>
	);
};
